import { useEffect, useRef } from 'react';

export interface UseTrackPaywallViewedOncePerOpenArgs {
  isOpen: boolean;
  /** Joined-teams query has settled — required before `hasTeam` is trustworthy. */
  isTeamsFetched: boolean;
  /** Team member role and billing flags are resolved — required when `hasTeam` is true. */
  isTeamDataReady: boolean;
  hasTeam: boolean;
  onTrack: () => void;
}

/**
 * Fires paywall-view analytics once per modal open, after team context is ready.
 * Avoids logging incomplete `is_admin` / `team_is_stripe_managed` when team queries
 * are still loading at open time.
 */
export function useTrackPaywallViewedOncePerOpen({
  isOpen,
  isTeamsFetched,
  isTeamDataReady,
  hasTeam,
  onTrack,
}: UseTrackPaywallViewedOncePerOpenArgs) {
  const hasTrackedRef = useRef(false);
  const onTrackRef = useRef(onTrack);
  onTrackRef.current = onTrack;

  useEffect(() => {
    if (!isOpen) {
      hasTrackedRef.current = false;
      return;
    }

    if (hasTrackedRef.current) return;

    const canIncludeTeamContext = isTeamsFetched && (!hasTeam || isTeamDataReady);
    if (!canIncludeTeamContext) return;

    hasTrackedRef.current = true;
    onTrackRef.current();
  }, [isOpen, isTeamsFetched, isTeamDataReady, hasTeam]);
}
